import { ParameterType } from '@/study-search/enums';
import { AllowedFilterCombination } from './AllowedFilterCombination';
import { Filter } from '../filter/Filter';
import {
	AttendanceFilterOptionValue,
	DegreeTypeFilterOptionValue,
	DurationFilterOptionValue,
	SpecialProgrammesFilterOptionValue
} from '@studyportals/search-filters';

class AllowedFilterCombinationsPolicy {
	private allowedCombinations: AllowedFilterCombination[] = [
		new AllowedFilterCombination([{ parameterType: ParameterType.DISCIPLINES }]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.COUNTRY }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.COUNTRY },
			{ parameterType: ParameterType.AREA }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.CONTINENT }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.DEGREE_TYPE }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.DURATION }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.ATTENDANCE }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.DELIVERY_METHOD }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.TUITION_FEE }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.TUITION_FEE },
			{ parameterType: ParameterType.DELIVERY_METHOD }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.EDUCATIONAL_FORM }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.EDUCATIONAL_FORM },
			{ parameterType: ParameterType.COUNTRY }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DISCIPLINES },
			{ parameterType: ParameterType.COUNTRY },
			{ parameterType: ParameterType.DEGREE_TYPE }
		]),
		new AllowedFilterCombination([{ parameterType: ParameterType.CONTINENT }]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.CONTINENT },
			{ parameterType: ParameterType.TUITION_FEE }
		]),
		new AllowedFilterCombination([{ parameterType: ParameterType.COUNTRY }]),
		new AllowedFilterCombination([{ parameterType: ParameterType.COUNTRY }, { parameterType: ParameterType.AREA }]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.COUNTRY },
			{ parameterType: ParameterType.DEGREE_TYPE }
		]),
		new AllowedFilterCombination([{ parameterType: ParameterType.COUNTRY }, { parameterType: ParameterType.DURATION }]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.COUNTRY },
			{ parameterType: ParameterType.ATTENDANCE }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.COUNTRY },
			{ parameterType: ParameterType.TUITION_FEE }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.COUNTRY },
			{ parameterType: ParameterType.EDUCATIONAL_FORM }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.COUNTRY },
			{ parameterType: ParameterType.DELIVERY_METHOD },
			{ parameterType: ParameterType.DEGREE_TYPE, value: DegreeTypeFilterOptionValue.MBA }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.COUNTRY },
			{ parameterType: ParameterType.DEGREE_TYPE, value: DegreeTypeFilterOptionValue.MBA },
			{ parameterType: ParameterType.DURATION, value: DurationFilterOptionValue.ONE_YEAR }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.COUNTRY },
			{ parameterType: ParameterType.DEGREE_TYPE, value: DegreeTypeFilterOptionValue.MSC },
			{ parameterType: ParameterType.DURATION, value: DurationFilterOptionValue.ONE_YEAR }
		]),
		new AllowedFilterCombination([{ parameterType: ParameterType.DELIVERY_METHOD }]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DELIVERY_METHOD },
			{ parameterType: ParameterType.DEGREE_TYPE }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.DELIVERY_METHOD, value: AttendanceFilterOptionValue.ONLINE },
			{ parameterType: ParameterType.COUNTRY }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.SPECIAL_PROGRAMMES, value: SpecialProgrammesFilterOptionValue.ERASMUS_MUNDUS }
		]),
		new AllowedFilterCombination([
			{ parameterType: ParameterType.SPECIAL_PROGRAMMES, value: SpecialProgrammesFilterOptionValue.JOINT_PROGRAMMES }
		]),
		new AllowedFilterCombination([{ parameterType: ParameterType.TUITION_FEE }])
	];

	public satisfiedForTitle(filters: Filter[]): boolean {
		const keywordWhatFilter = filters.filter((_) => _.type === ParameterType.KEYWORD_WHAT)[0];
		const keywordWhereFilter = filters.filter((_) => _.type === ParameterType.KEYWORD_WHERE)[0];

		if (keywordWhatFilter !== undefined && keywordWhatFilter.values.length > 0) {
			return true;
		}

		if (keywordWhereFilter !== undefined && keywordWhereFilter.values.length > 0) {
			return true;
		}

		const candidateCombination: Filter[] = filters.filter((_) => _.canBeIndexed());
		if (candidateCombination.length === 0) {
			return false;
		}

		if (this.locationNotIndexable(candidateCombination, filters)) {
			return false;
		}

		return this.allowedCombinations.some((_) => _.hasSameParameterTypes(candidateCombination));
	}

	public satisfiedBy(filters: Filter[]): boolean {
		const candidateCombination: Filter[] = filters.filter((_) => _.canBeIndexed());
		if (candidateCombination.length === 0) {
			return false;
		}

		if (this.locationNotIndexable(candidateCombination, filters)) {
			return false;
		}

		return this.allowedCombinations.some((_) => _.hasSameParameterTypes(candidateCombination));
	}

	public satisfiedForIndexability(filters: Filter[]): boolean {
		const candidateCombination: Filter[] = filters.filter((_) => _.values.length > 0);

		return candidateCombination.every(
			(_) =>
				(_.canBeIndexed() &&
					this.allowedCombinations.some((comb) => comb.hasSameParameterTypes(candidateCombination))) ||
				candidateCombination.length === 0
		);
	}

	public notSatisfiedBy(filters: Filter[]): boolean {
		return !this.satisfiedBy(filters);
	}

	private locationNotIndexable(candidateCombination: Filter[], filters: Filter[]): boolean {
		return (
			candidateCombination.some((_) => _.type === ParameterType.COUNTRY) &&
			filters.some((_) => _.type === ParameterType.AREA && _.values.length > 1)
		);
	}
}

export default new AllowedFilterCombinationsPolicy();
